import { useMemo } from "react";

import { RewardI } from "@/api/routes/incentives";
import Router from "@/helpers/router";
import type { WhatsNewSourceI } from "@/modules/whats-new/data/use-whats-new-cards";
import { resolveWhatsNewEntry } from "@/modules/whats-new/lib/audience";
import {
  WHATS_NEW_ICONS,
  WhatsNewAudienceT,
  WhatsNewCardI,
} from "@/modules/whats-new/lib/types";

import {
  CADENCE_LABELS,
  describePrize,
  describeRules,
  formatEtLastDay,
} from "../lib/format";
import { REWARDS_LAUNCH_ENTRY } from "../lib/launch";
import { useFetchRewardAnnouncements } from "./queries";

const MAX_RULE_LINES = 6;

/** A reward with no start date counts from its creation (the server's
 * announce window); its card only needs to be live already. */
const ALWAYS_STARTED = "1970-01-01T00:00:00.000Z";

/**
 * A running reward as a What's new card (C-16 S1, C16-D330): live until
 * `announce_until`, which the server sends only inside the window. The id
 * carries the start (else the window's end), so a reward that is rescheduled
 * announces again and an unchanged one stays dismissed.
 */
export const rewardWhatsNewCard = (reward: RewardI): WhatsNewCardI | null => {
  if (!reward.announce_until) {
    return null;
  }

  const when = reward.end_at
    ? `Runs through ${formatEtLastDay(reward.end_at)} (ET).`
    : `${CADENCE_LABELS[reward.cadence]}, until it's switched off.`;

  return {
    id: `incentive:${reward.id}:${reward.start_at ?? reward.announce_until}`,
    startsAt: reward.start_at ?? ALWAYS_STARTED,
    endsAt: reward.announce_until,
    title: `New contest: ${reward.name}`,
    intro: reward.description ?? undefined,
    sections: [
      {
        id: `incentive-${reward.id}`,
        icon: WHATS_NEW_ICONS.TROPHY,
        title: describePrize(reward),
        items: [...describeRules(reward).slice(0, MAX_RULE_LINES), when],
        link: { label: "See your standing", href: Router.reward(reward.id) },
      },
    ],
  };
};

/**
 * Every card the Rewards source announces for `audience`: Rewards' launch
 * entry while `rewards_enabled` is on, then one card per running reward in
 * its announce window.
 */
export const resolveRewardWhatsNewCards = (
  audience: WhatsNewAudienceT,
  isRewardsOn: boolean,
  rewards: Array<RewardI>
): Array<WhatsNewCardI> =>
  [
    isRewardsOn ? resolveWhatsNewEntry(REWARDS_LAUNCH_ENTRY, audience) : null,
    ...rewards.map(rewardWhatsNewCard),
  ].filter((card): card is WhatsNewCardI => card !== null);

/**
 * What's new source for C-16 S1. Settles on every outcome (off, error, no
 * rewards), so it never holds the other sources back.
 */
export const useRewardWhatsNewCards = (
  audience: WhatsNewAudienceT | null
): WhatsNewSourceI => {
  const announcements = useFetchRewardAnnouncements(!!audience);
  const isLoading =
    announcements.isPending && announcements.fetchStatus !== "idle";

  return useMemo(() => {
    if (!audience || isLoading) {
      return { cards: [], isSettled: false };
    }

    return {
      cards: resolveRewardWhatsNewCards(
        audience,
        !!announcements.data?.isOn,
        announcements.data?.rewards ?? []
      ),
      isSettled: true,
    };
  }, [audience, isLoading, announcements.data]);
};
